import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import BookingForm from "@/components/BookingForm";
import SEO from "@/components/SEO";
import { RevealLines } from "@/components/Reveal";
import { IMAGES, CAMPUSPATH_URL, TESTGUARD_URL, LOGO, TG_LOGO, OG_IMAGE_CAMPUSPATH, OG_IMAGE_TESTGUARD, CONTACT_EMAIL, resolveProduct } from "@/lib/constants";

const EASE = [0.16, 1, 0.3, 1];

export default function Demo({ product: forcedProduct }) {
  const [params] = useSearchParams();
  const product = forcedProduct || resolveProduct(params.get("product"));
  const isTG = product === "testguard";
  const brand = isTG ? "TestGuard" : "CampusPath";

  const pitch = isTG
    ? "Book a personalized walkthrough. We'll tailor it to your exam security, device monitoring and proctoring needs, and answer every question."
    : "Book a personalized walkthrough. We'll tailor it to your school's dismissal, attendance and communication needs, and answer every question.";

  return (
    <main className={`min-h-screen ${isTG ? "dark-scroll bg-obsidian text-white" : "bg-bone"}`} data-testid="demo-page">
      <SEO
        title={`Schedule a ${brand} Demo | ${brand}`}
        description={`Book a personalized ${brand} demo. We'll tailor a live walkthrough to your needs.`}
        canonical={`${isTG ? TESTGUARD_URL : CAMPUSPATH_URL}/demo`}
        image={isTG ? OG_IMAGE_TESTGUARD : OG_IMAGE_CAMPUSPATH}
        favicon={isTG ? TG_LOGO : LOGO}
      />
      <SiteNav dark={isTG} />
      <section className="mx-auto grid max-w-7xl gap-16 px-6 pt-36 pb-24 md:px-8 md:pt-44 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <span className="text-xs font-600 uppercase tracking-widest text-leaf-600">{brand} · Schedule a Demo</span>
          <h1 className={`mt-5 font-display text-[clamp(2.4rem,5.5vw,4rem)] font-900 leading-[0.96] tracking-tightest ${isTG ? "text-white" : "text-ink"}`}>
            <RevealLines lines={isTG ? ["See it lock", "down, before", "the first exam."] : ["See it run,", "before the", "first bell."]} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.5 }}
            className={`mt-7 max-w-md text-lg leading-relaxed ${isTG ? "text-white/65" : "text-ink/65"}`}
          >
            {pitch}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.75 }}
            className="mt-12 overflow-hidden rounded-2xl"
          >
            <img src={isTG ? IMAGES.examLaptop : IMAGES.teacher} alt={isTG ? "Secured exam session" : "Teacher using CampusPath"} className="h-64 w-full object-cover" />
          </motion.div>

          <div className={`mt-8 rounded-2xl border p-6 ${isTG ? "border-white/10 bg-surface-dark" : "border-ink/10 bg-white"}`}>
            <div className={`font-display text-lg font-800 ${isTG ? "text-white" : "text-ink"}`}>Prefer to talk now?</div>
            <a href={`mailto:${CONTACT_EMAIL}`} className="mt-2 inline-block text-leaf-600 underline decoration-leaf/40 underline-offset-4" data-testid="demo-email-link">
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <div className={
            isTG
              ? "rounded-[2rem] border border-leaf/25 bg-surface-dark p-8 shadow-[0_0_90px_-20px_rgba(0,200,83,0.5)] ring-1 ring-inset ring-white/5 md:p-10"
              : "rounded-[2rem] border border-ink/10 bg-white p-8 shadow-[0_40px_80px_-40px_rgba(0,34,68,0.35)] md:p-10"
          }>
            <h2 className={`font-display text-2xl font-800 ${isTG ? "text-white" : "text-ink"}`}>Book your demo</h2>
            <p className={`mt-2 text-sm ${isTG ? "text-white/50" : "text-ink/50"}`}>Pick a time that works, you'll get instant confirmation by email.</p>
            <div className="mt-8">
              <BookingForm product={product} dark={isTG} />
            </div>
          </div>
        </div>
      </section>
      <SiteFooter dark={isTG} />
    </main>
  );
}
